import { PATH_ROUTES } from "../router/routes";
import { type PreguntaId } from "./preguntaPaths";

export interface FontSource {
  label: string;
  url: string;
  download?: boolean;
}

export interface SidebarContent {
  question: string;
  periodYears: string;
  dimensions: string[];
  fontSources: FontSource[];
}

export interface ChartConfig {
  type: "line" | "bar";
  preguntaId: PreguntaId;
}

export interface ModulePageConfig {
  title: string;
  navLabel: string;
  sidebar: SidebarContent;
  chartConfig?: ChartConfig;
}

export const MODULE_PAGE_CONFIG: Record<string, ModulePageConfig> = {
  [PATH_ROUTES.titulacions]: {
    title: "Distribució de matriculacions per àmbit d’estudi",
    navLabel: "Titulacions",
    chartConfig: { type: "line", preguntaId: "p1" },
    sidebar: {
      question:
        "Quines diferències hi ha en la distribució percentual de la matrícula universitària per àmbit d’estudi entre Catalunya, Espanya i la UE, i com evolucionen al llarg dels cursos?",
      periodYears: "8 cursos acadèmics",
      dimensions: [
        "Arts i humanitats",
        "Ciències",
        "Ciències de la salut",
        "Ciències socials i jurídiques",
        "Enginyeria i arquitectura"
      ],
      fontSources: [
        { label: "www.idescat.cat", url: "https://www.idescat.cat/indicadors/?id=aec&n=15730" },
        { label: "estadisticas.ciencia.gob.es", url: "https://estadisticas.ciencia.gob.es/jaxiPx/Tabla.htm?path=/Universitaria/Alumnado/EEU_2024/GradoCiclo/Matriculados/l0/&file=1_3_Mat_Sex_Ded_Rama_Tot.px&L=0" },
        { label: "ec.europa.eu", url: "https://ec.europa.eu/eurostat/databrowser/view/educ_uoe_enrt03__custom_19646703/default/table" },
      ],
    },
  },
  [PATH_ROUTES.professionsRegulades]: {
    title: "Ocupats per sector per cada 1.000 habitants",
    navLabel: "Professions regulades",
    chartConfig: { type: "line", preguntaId: "p2" },
    sidebar: {
      question:
        "Com es posiciona Catalunya en la presència de professionals vinculats a determinats sectors, en relació amb la població total, en comparació amb altres països europeus?",
      periodYears: "10 anys",
      dimensions: [
        "Agricultura",
        "Construcció",
        "Indústria",
        "Serveis",
      ],
      fontSources: [
        { label: "ec.europa.eu", url: "https://ec.europa.eu/eurostat/databrowser/view/lfsa_egan2/default/table?lang=en&category=labour.employ.lfsa.lfsa_emp" },
        { label: "ec.europa.eu", url: "https://ec.europa.eu/eurostat/databrowser/view/demo_pjan/default/table?lang=en" },
        { label: "www.idescat.cat", url: "https://www.idescat.cat/indicadors/?id=basics&n=10204&lang=es" },
        { label: "www.idescat.cat", url: "https://www.idescat.cat/indicadors/?id=basics&n=10210&lang=es" },
        { label: "www.idescat.cat", url: "https://www.idescat.cat/indicadors/?id=basics&n=10328&lang=es&tema=xifpo" },
      ],
    },
  },
  [PATH_ROUTES.professionalsSanitaris]: {
    title: "Projecció de demanda futura de professionals del sector sanitari",
    navLabel: "Professionals sanitaris",
    chartConfig: { type: "line", preguntaId: "p3" },
    sidebar: {
      question:
        "Quina és la projecció de la demanda de professionals a Catalunya, per sectors productius i amb especial rellevància de l'àmbit sanitari, per als propers 10 i 20 anys, tenint en compte l’evolució demogràfica (naixements i creixement poblacional) i l’índex d’envelliment (jubilacions). I quin desajust existeix entre aquesta demanda i l’oferta actual, mesurada pel nombre de graduats anuals i les places de nou accés autoritzades per cada titulació?",
      periodYears: "20 anys",
      dimensions: ["Sanitaris", "Any"],
      fontSources: [
        { label: "w6.seg-social.es", url: "https://w6.seg-social.es/PXWeb/pxweb/es/Afiliados%20en%20alta%20laboral/Afiliados%20en%20alta%20laboral__Afiliados%20Medios/04mb.%20Por%20CC.AA%2C%20%20seccion%20de%20actividad%20y%20regimen%20%28RG%20y%20RETA%29.px/" },
        { label: "www.idescat.cat", url: "https://www.idescat.cat/indicadors/?id=aec&n=15223&" },
        { label: "www.idescat.cat", url: "https://www.idescat.cat/pub/?id=proj&n=20955&t=205000" },
        { label: "www.idescat.cat", url: "https://www.idescat.cat/indicadors/?id=aec&n=15980&t=202400" },
        { label: "w6.seg-social.es", url: "https://w6.seg-social.es/PXWeb/pxweb/es/Afiliados%20en%20alta%20laboral/Afiliados%20en%20alta%20laboral__Afiliados%20Fin%20de%20mes/09f%20Altas%20y%20Bajas%20R.%20GENERAL%20por%20CNAE%20a%202%20d%c3%adgitos.px/table/tableViewLayout1/" },
        { label: "w6.seg-social.es", url: "https://w6.seg-social.es/PXWeb/pxweb/es/Afiliados%20en%20alta%20laboral/Afiliados%20en%20alta%20laboral__Afiliados%20Medios/02m.%20Por%20sexo,%20CNAE%20a%20dos%20digitos%20y%20regimen.px/table/tableViewLayout1/" },
        { label: "w6.seg-social.es", url: "https://w6.seg-social.es/PXWeb/pxweb/es/Afiliados%20en%20alta%20laboral/Afiliados%20en%20alta%20laboral__Afiliados%20Medios/04mb.%20Por%20CC.AA%2C%20%20seccion%20de%20actividad%20y%20regimen%20%28RG%20y%20RETA%29.px/" },
        { label: "w6.seg-social.es", url: "https://w6.seg-social.es/PXWeb/pxweb/es/Afiliados%20en%20alta%20laboral/Afiliados%20en%20alta%20laboral__Afiliados%20Medios/04mb.%20Por%20CC.AA%2C%20%20seccion%20de%20actividad%20y%20regimen%20%28RG%20y%20RETA%29.px/" },
      ],
    },
  },
  [PATH_ROUTES.ofertaUniversitaria]: {
    title: "Desajust laboral per titulació universitària a Catalunya",
    navLabel: "Oferta universitaria",
    chartConfig: { type: "bar", preguntaId: "p4" },
    sidebar: {
      question:
        "Quines titulacions universitàries a Catalunya presenten majors nivells de desajust laboral, considerant conjuntament la taxa d’ocupació, la sobrequalificació i l’adequació entre estudis i feina?",
      periodYears: "2023",
      dimensions: ["Graus universitaris"],
      fontSources: [
        { label: "estudis.aqu.cat", url: "https://estudis.aqu.cat/euc/es/Dades/Enquestes" },
        { label: "Titulacions per curs 2019-2020 a 2025-2026.xlsx", url: "Titulacions per curs 2019-2020 a 2025-2026.xlsx", download: true },
      ],
    },
  },
  [PATH_ROUTES.desajustTitulacio]: {
    title: "Oferta universitària vs ocupació sectorial a Catalunya",
    navLabel: "Desajust per titulació",
    chartConfig: { type: "bar", preguntaId: "p5" },
    sidebar: {
      question:
        "Com es distribueixen els sectors productius en relació amb l’oferta universitària a les comarques catalanes, i quin grau d’alineació existeix entre el pes de les places formatives i el pes de l’ocupació per sector?",
      periodYears: "2025",
      dimensions: ["Comarques", "Sectors productius"],
      fontSources: [
        { label: "www.idescat.cat", url: "https://www.idescat.cat/codis/?id=50&n=9&" },
        { label: "www.idescat.cat", url: "https://www.idescat.cat/pub/?id=afi&n=10602&hist=taules%2Fv2%2Fafi%2F10602%2F11111%2Fcom%2Fdata%3F_LAST_%3D22%5Ec%3D3%2Fr%3D1%2C2%2Ft%3D-1c%3B0d%2C21%3B-2c%3B-3c%2Fe%3D1" },
        { label: "20260130_placesROC.xlsx", url: "20260130_placesROC.xlsx", download: true },
      ],
    },
  },
};

export const getModuleConfig = (path: string): ModulePageConfig =>
  MODULE_PAGE_CONFIG[path] ?? MODULE_PAGE_CONFIG[PATH_ROUTES.titulacions];
